/**
 * Slideshow custom element — the Concept hero carousel, reimplemented.
 *
 * The geometry lives in CSS (component-slideshow.css): cells are page-container
 * wide with a 30px gap inside a full-bleed clipping element, so the neighbours
 * peek at the viewport edges. This script only translates the track so the
 * selected cell is centred, and keeps the content overlay, dots, arrows and
 * autoplay in step with the selection.
 *
 * Wrap behaviour: advancing past either end keeps travelling in the same
 * direction — the track never rewinds across every slide. Concept runs
 * flickity, which physically re-slots cells across the seam; here the same
 * effect comes from temporary clones: the wrap target (plus its onward
 * neighbour, so the leading sliver stays populated) is cloned onto the end
 * being crossed, the track animates one slot further, and on transition end
 * the clones are discarded and the track re-sits on the real cell with the
 * transition suppressed. Reduced motion skips the theatre and jumps directly.
 *
 * Guarded like component-marquee.js: the script tag is emitted per section
 * instance, and a second execution must not redeclare anything top-level.
 */
if (!customElements.get('slideshow-element')) {
  customElements.define('slideshow-element', class extends HTMLElement {

    constructor() {
      super();
      this.track = this.querySelector('.slideshow__track');
      this.slides = this.querySelectorAll('.slideshow__slide');
      this.dots = this.querySelectorAll('.slideshow__dot');
      this.words = this.querySelectorAll('.slideshow__word');
      this.prevButton = this.querySelector('[data-prev]');
      this.nextButton = this.querySelector('[data-next]');
      // Which slide the section chose to open on. The markup is already rendered in that state,
      // so this only tells the track where to sit; parsed defensively because the attribute is
      // authored in Liquid and a section can legitimately have fewer slides than it names.
      this.startIndex = parseInt(this.dataset.startIndex, 10);
      if (!Number.isInteger(this.startIndex) || this.startIndex < 0 || this.startIndex >= this.slides.length) {
        this.startIndex = 0;
      }
      this.currentIndex = this.startIndex;
      this.autoplayTimer = null;
      this.autoplaySpeed = parseInt(this.dataset.speed, 10) || 7000;
      this.isAutoplay = this.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isIntersecting = true;
      this.paused = false;
      this.wrapClones = null;
      this.wrapTimer = null;
      this.onWrapEnd = (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.finishWrap();
      };

      this.init();
    }

    init() {
      if (!this.track || this.slides.length === 0) return;

      this.classList.add('slideshow--initialized');

      this.select(this.startIndex, false);

      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.step(-1));
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.step(1));
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      this.addEventListener('keydown', this.handleKeydown.bind(this));
      this.initSwipe();

      // Re-centre on resize without animating the correction.
      this.resizeObserver = new ResizeObserver(() => this.position(false));
      this.resizeObserver.observe(this);

      this.addEventListener('pointerenter', () => { this.paused = true; });
      this.addEventListener('pointerleave', () => { this.paused = false; });
      this.addEventListener('focusin', () => { this.paused = true; });
      this.addEventListener('focusout', () => { this.paused = false; });

      if (this.isAutoplay) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.isIntersecting = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.startAutoplay();
            } else {
              this.stopAutoplay();
            }
          });
        }, { threshold: 0.5 });

        this.observer.observe(this);
      }

      if (this.reducedMotion) {
        this.isAutoplay = false;
      }

      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotionQuery.addEventListener('change', (e) => {
        this.reducedMotion = e.matches;
        if (this.reducedMotion) {
          this.stopAutoplay();
        } else if (this.isIntersecting && this.dataset.autoplay === 'true') {
          this.startAutoplay();
        }
      });

      if (this.isAutoplay && !this.reducedMotion) {
        this.startAutoplay();
      }
    }

    /* Translate the track so the cell occupying `slot` (wrap clones included) is centred. */
    positionAt(slot, animate = true) {
      const cell = this.slides[0];
      if (!cell) return;
      const gap = parseFloat(getComputedStyle(cell).marginInlineEnd) || 0;
      const x = (this.clientWidth - cell.offsetWidth) / 2
        - slot * (cell.offsetWidth + gap);

      if (!animate) this.track.style.transition = 'none';
      this.track.style.transform = `translate3d(${x}px, 0, 0)`;
      if (!animate) {
        // Flush so re-enabling the transition doesn't animate this move.
        this.track.getBoundingClientRect();
        this.track.style.transition = '';
      }
    }

    position(animate = true) {
      // A move or resize landing mid-wrap completes the wrap first, so the slot
      // arithmetic below is always against the real, clone-free track.
      if (this.wrapClones) this.finishWrap();
      this.positionAt(this.currentIndex, animate);
    }

    select(index, animate = true) {
      this.currentIndex = index;
      this.position(animate);
      this.updateSelection(index);
    }

    /* Selection state only — slide classes, aria, dots, words. No movement. */
    updateSelection(index) {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-selected', i === index);
        if (i === index) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-selected', i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      this.words.forEach((word, i) => {
        word.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    goTo(index) {
      if (index === this.currentIndex) return;
      this.select(index);

      if (this.isAutoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    /*
     * Directional move: crossing either end wraps seamlessly in the direction of
     * travel instead of translating back across the whole track.
     */
    advance(direction) {
      const count = this.slides.length;
      if (count < 2) return;
      this.finishWrap();
      const target = (this.currentIndex + direction + count) % count;
      const wraps = direction === 1 ? target === 0 : target === count - 1;

      if (wraps && !this.reducedMotion) {
        this.wrapTo(target, direction);
        if (this.isAutoplay) {
          this.stopAutoplay();
          this.startAutoplay();
        }
      } else {
        this.goTo(target);
      }
    }

    /*
     * Seamless wrap: clone the target cell (and the one after it in the travel
     * direction, so the sliver ahead of it is populated) past the end being
     * crossed, animate one slot further the same way, then discard the clones
     * and re-sit the track on the real cell with the transition suppressed.
     */
    wrapTo(target, direction) {
      const count = this.slides.length;
      const makeGhost = (index) => {
        const ghost = this.slides[index].cloneNode(true);
        ghost.classList.remove('is-selected');
        ghost.setAttribute('aria-hidden', 'true');
        ghost.setAttribute('inert', '');
        return ghost;
      };

      const targetGhost = makeGhost(target);
      let animateToSlot;
      if (direction === 1) {
        const onward = makeGhost((target + 1) % count);
        this.track.append(targetGhost, onward);
        this.wrapClones = [targetGhost, onward];
        animateToSlot = count;
      } else {
        const onward = makeGhost((target - 1 + count) % count);
        this.track.prepend(onward, targetGhost);
        this.wrapClones = [onward, targetGhost];
        // Prepending pushed every real cell two slots right; re-sit on the
        // current cell's new slot before animating so nothing visibly jumps.
        this.positionAt(this.currentIndex + 2, false);
        animateToSlot = 1;
      }

      this.currentIndex = target;
      // Class changes land a frame after insertion: a newly-inserted node given
      // is-selected immediately would render straight in its settled state, so
      // waiting lets the ghost's zoom reveal transition — in step with the real
      // target's, which settles off-screen ready for the hand-off.
      requestAnimationFrame(() => {
        if (!this.wrapClones) return;
        targetGhost.classList.add('is-selected');
        this.updateSelection(target);
        this.positionAt(animateToSlot);
      });

      this.track.addEventListener('transitionend', this.onWrapEnd);
      // Fallback for a transitionend that never fires (e.g. the tab hidden
      // mid-animation suppresses it in some engines).
      const duration = parseFloat(getComputedStyle(this.track).transitionDuration) * 1000 || 600;
      this.wrapTimer = setTimeout(() => this.finishWrap(), duration + 200);
    }

    finishWrap() {
      if (!this.wrapClones) return;
      clearTimeout(this.wrapTimer);
      this.track.removeEventListener('transitionend', this.onWrapEnd);
      this.wrapClones.forEach((ghost) => ghost.remove());
      this.wrapClones = null;
      this.positionAt(this.currentIndex, false);
    }

    next() {
      this.advance(1);
    }

    prev() {
      this.advance(-1);
    }

    step(direction) {
      this.paused = true;
      this.advance(direction);
    }

    handleKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.prev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next();
      }
    }

    startAutoplay() {
      if (!this.isAutoplay || this.reducedMotion || !this.isIntersecting) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        if (!this.paused && this.isIntersecting && !document.hidden) {
          this.next();
        }
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    initSwipe() {
      let startX = 0;
      let startY = 0;
      let endX = 0;
      let endY = 0;

      this.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        endX = startX;
        endY = startY;
      }, { passive: true });

      this.addEventListener('touchmove', (e) => {
        endX = e.touches[0].clientX;
        endY = e.touches[0].clientY;
      }, { passive: true });

      this.addEventListener('touchend', () => {
        const diffX = startX - endX;
        const diffY = startY - endY;

        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
          if (diffX > 0) {
            this.next();
          } else {
            this.prev();
          }
        }
      }, { passive: true });
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.wrapTimer);
      if (this.observer) this.observer.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }
  });
}
